import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Badge, Button, Dropdown, OverlayTrigger, Tooltip } from 'react-bootstrap';
import { Edit2, Ellipsis, Eye, EyeOff, Trash2 } from 'lucide-react';
import moment from 'moment';
import { toast } from 'sonner';
import { ABORT_ERROR, makeRESTApiRequest } from '../../helpers/api';
import { ENDPOINTS } from '../../helpers/constants';
import DataTable from '../common/data-table/DataTable';
import AlertModal from '../AlertModal';
import DirectoryAssociationModal from '../hoaForm/DirectoryAssociationModal';
import { resolveAssociationManagementType, resolveEntityId } from './HoaDirectoryHelper';

const ACTION = {
  EDIT: 'Edit',
  ARCHIVE: 'Archive',
  UNARCHIVE: 'Unarchive',
  DELETE: 'Delete',
};

const TruncatedCell = ({ text, className, children, tooltipId }) => {
  const title = text || '';
  const content = (
    <div className={`text-truncate ${className || ''}`} style={{ maxWidth: '100%' }}>
      {children ?? title}
    </div>
  );

  if (!title) return content;

  return (
    <OverlayTrigger
      placement="top"
      delay={50}
      overlay={
        <Tooltip className="custom-tooltip" id={tooltipId} style={{ maxWidth: 360 }}>
          {title}
        </Tooltip>
      }
    >
      {content}
    </OverlayTrigger>
  );
};

const toTableRow = (association, previous = {}) => ({
  ...previous,
  _id: association._id,
  name: association.name,
  mgmtId: association.mgmtId
    ? { _id: association.mgmtId._id || association.mgmtId, name: association.mgmtId.name }
    : null,
  managementType: association.managementType ?? null,
  orderingPlatform: association.orderingPlatform || '',
  noEstoppelNeeded: Boolean(association.noEstoppelNeeded),
  isHidden: Boolean(association.isHidden),
  updatedAt: association.updatedAt,
  updatedBy: association.updatedBy,
  hasOrderAssigned: previous.hasOrderAssigned ?? Boolean(association.hasOrderAssigned),
});

const rowMatchesVisibility = (row, visibility) => {
  if (visibility === 'hidden') return Boolean(row.isHidden);
  if (visibility === 'visible') return !row.isHidden;
  return true;
};

const AssociationsTable = ({
  searchQuery,
  visibility = 'all',
  selectedAssociationId,
  onAssociationSelect,
  onAssociationUpdate,
  onAssociationDeleted,
  setActiveTabItemsMetadata,
}) => {
  const [associations, setAssociations] = useState([]);
  const [loading, setLoading] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [hasMore, setHasMore] = useState(true);
  const [pageNumber, setPageNumber] = useState(1);
  const [totalCount, setTotalCount] = useState(0);
  const [sortConfig, setSortConfig] = useState({ key: 'name', direction: 'asc' });
  const [editingAssociation, setEditingAssociation] = useState(null);
  const [hideCandidate, setHideCandidate] = useState(null);
  const [hideFromBlockedDelete, setHideFromBlockedDelete] = useState(false);
  const [deleteCandidate, setDeleteCandidate] = useState(null);
  const [actionLoading, setActionLoading] = useState(false);
  const abortControllerRef = useRef(null);

  const PAGE_SIZE = 20;

  const decrementTotalCount = useCallback(() => {
    setTotalCount(prev => {
      const next = Math.max(0, prev - 1);
      if (setActiveTabItemsMetadata) {
        setActiveTabItemsMetadata({ count: next, loading: false });
      }
      return next;
    });
  }, [setActiveTabItemsMetadata]);

  const fetchAssociations = useCallback(
    async (page, query, sort, isLoadMore = false) => {
      let signal;
      if (!isLoadMore) {
        abortControllerRef.current?.abort();
        const controller = new AbortController();
        abortControllerRef.current = controller;
        signal = controller.signal;
      }

      if (isLoadMore) {
        setLoadingMore(true);
      } else {
        setLoading(true);
        if (setActiveTabItemsMetadata) {
          setActiveTabItemsMetadata({ count: 0, loading: true });
        }
      }

      const { response, error } = await makeRESTApiRequest({
        endpoint: ENDPOINTS.ASSOCIATIONS_SEARCH,
        method: 'POST',
        requestBody: {
          query: query || '',
          pageNumber: page,
          pageSize: PAGE_SIZE,
          sortBy: sort.key,
          sortDir: sort.direction,
          visibility,
        },
        signal,
      });

      if (error === ABORT_ERROR) {
        if (isLoadMore) {
          setLoadingMore(false);
        } else {
          setLoading(false);
        }
        return;
      }

      if (error) {
        toast.error(error);
        setLoading(false);
        setLoadingMore(false);
        return;
      }

      if (response) {
        const { results = [], resultCount, totalPages } = response;

        if (isLoadMore) {
          setAssociations(prev => [...prev, ...results]);
        } else {
          setAssociations(results);
        }

        setTotalCount(resultCount);
        setHasMore(page < totalPages);

        if (setActiveTabItemsMetadata && !isLoadMore) {
          setActiveTabItemsMetadata({ count: resultCount, loading: false });
        }
      }

      setLoading(false);
      setLoadingMore(false);
    },
    [setActiveTabItemsMetadata, visibility]
  );

  useEffect(() => {
    setPageNumber(1);
    setHasMore(true);
    fetchAssociations(1, searchQuery, sortConfig, false);
  }, [searchQuery, sortConfig, visibility, fetchAssociations]);

  useEffect(
    () => () => {
      abortControllerRef.current?.abort();
    },
    []
  );

  const handleLoadMore = useCallback(() => {
    if (loading || loadingMore || !hasMore) return;
    const nextPage = pageNumber + 1;
    setPageNumber(nextPage);
    fetchAssociations(nextPage, searchQuery, sortConfig, true);
  }, [loading, loadingMore, hasMore, pageNumber, searchQuery, sortConfig, fetchAssociations]);

  const handleSortChange = useCallback(newSortConfig => {
    setSortConfig(newSortConfig);
    setPageNumber(1);
  }, []);

  const applyRowUpdate = useCallback(
    (updatedRow, { removeIfFiltered = false } = {}) => {
      const shouldRemove = removeIfFiltered && !rowMatchesVisibility(updatedRow, visibility);

      setAssociations(prev => {
        if (shouldRemove) {
          return prev.filter(row => row._id !== updatedRow._id);
        }
        return prev.map(row => (row._id === updatedRow._id ? updatedRow : row));
      });

      if (shouldRemove) {
        decrementTotalCount();
      }

      onAssociationUpdate?.(updatedRow);
    },
    [visibility, decrementTotalCount, onAssociationUpdate]
  );

  const handleEdit = useCallback(row => {
    setEditingAssociation(row);
  }, []);

  const handleEditSave = updatedAssociation => {
    const previous = associations.find(row => row._id === updatedAssociation._id) || {};
    applyRowUpdate(toTableRow(updatedAssociation, previous));
    setEditingAssociation(null);
  };

  const hideAssociation = async row => {
    if (!row) return false;

    setActionLoading(true);
    const nextHidden = !row.isHidden;

    try {
      const { response, error } = await makeRESTApiRequest({
        endpoint: ENDPOINTS.ASSOCIATION_WITH_ID(row._id),
        method: 'PUT',
        requestBody: { isHidden: nextHidden },
      });

      if (error) {
        toast.error(error);
        return false;
      }

      toast.success(nextHidden ? 'Association archived' : 'Association unarchived');
      applyRowUpdate(toTableRow(response, row), { removeIfFiltered: true });
      return true;
    } finally {
      setActionLoading(false);
    }
  };

  const closeHideModal = () => {
    if (actionLoading) return;
    setHideCandidate(null);
    setHideFromBlockedDelete(false);
  };

  const handleConfirmHide = async () => {
    const success = await hideAssociation(hideCandidate);
    if (success) {
      setHideCandidate(null);
      setHideFromBlockedDelete(false);
    }
  };

  const handleConfirmDelete = async () => {
    if (!deleteCandidate) return;

    setActionLoading(true);
    try {
      const { error } = await makeRESTApiRequest({
        endpoint: ENDPOINTS.ASSOCIATION_WITH_ID(deleteCandidate._id),
        method: 'DELETE',
      });

      if (error) {
        toast.error(error);
        return;
      }

      toast.success('Association deleted');
      setAssociations(prev => prev.filter(row => row._id !== deleteCandidate._id));
      decrementTotalCount();
      onAssociationDeleted?.(deleteCandidate._id);
      setDeleteCandidate(null);
    } finally {
      setActionLoading(false);
    }
  };

  const handleActionSelect = (label, row) => {
    if (label === ACTION.EDIT) {
      handleEdit(row);
      return;
    }
    if (label === ACTION.ARCHIVE || label === ACTION.UNARCHIVE) {
      setHideFromBlockedDelete(false);
      setHideCandidate(row);
      return;
    }
    if (label === ACTION.DELETE) {
      if (row.hasOrderAssigned) {
        setHideFromBlockedDelete(true);
        setHideCandidate(row);
        return;
      }
      setDeleteCandidate(row);
    }
  };

  const columns = [
    {
      key: 'name',
      label: 'Association',
      sortable: true,
      width: '180',
      cellRenderer: row => (
        <div className="d-flex align-items-center" style={{ minWidth: 0, maxWidth: '100%' }}>
          <div style={{ minWidth: 0, flex: 1 }}>
            <TruncatedCell text={row.name} className="fw-bold text-dark" tooltipId={`assoc-name-${row._id}`} />
          </div>
          {row.isHidden && (
            <Badge
              className="ml-1 px-2 py-1 smallFont flex-shrink-0 bg-danger-light text-danger"
            >
              Archived
            </Badge>
          )}
        </div>
      ),
    },
    {
      key: 'managementCompany',
      label: 'Management Company',
      sortable: true,
      width: '180',
      valueSelector: row => {
        const type = resolveAssociationManagementType(row);
        if (type === 'managed') return row.mgmtId?.name || '';
        if (type === 'self') return 'Self-managed';
        return 'Not set';
      },
      cellRenderer: row => {
        const type = resolveAssociationManagementType(row);
        const companyName = row.mgmtId?.name;
        const fallback = type === 'self' ? 'Self-managed' : 'Not set';
        return (
          <TruncatedCell
            text={companyName || fallback}
            className="text-muted smallFont"
            tooltipId={`assoc-mgmt-${row._id}`}
          >
            {companyName || <em>{fallback}</em>}
          </TruncatedCell>
        );
      },
    },
    {
      key: 'type',
      label: 'Type',
      sortable: true,
      width: '100',
      cellRenderer: row => {
        const resolved = resolveAssociationManagementType(row);
        if (!resolved) {
          return (
            <Badge className="px-2 py-1 smallFont bg-grey text-muted">
              Not set
            </Badge>
          );
        }
        const type = resolved === 'managed' ? 'Managed' : 'Self-managed';
        const background = resolved === 'managed' ? '#e8eeff' : '#e8f5e9';
        const color = resolved === 'managed' ? '#1F4E8C' : '#2E7D32';
        return (
          <Badge className="px-2 py-1 smallFont" style={{ background, color }}>
            {type}
          </Badge>
        );
      },
    },
    {
      key: 'orderingPlatform',
      label: 'Ordering Platform',
      sortable: true,
      width: '140',
      cellRenderer: row => (
        <TruncatedCell
          text={row.orderingPlatform || ''}
          className="text-muted smallFont"
          tooltipId={`assoc-platform-${row._id}`}
        >
          {row.orderingPlatform || <span className="text-muted">—</span>}
        </TruncatedCell>
      ),
    },
    {
      key: 'updatedAt',
      label: 'Last Updated',
      sortable: true,
      width: '160',
      cellRenderer: row => {
        if (!row.updatedAt) {
          return <span className="text-muted smallFont">—</span>;
        }

        const date = moment(row.updatedAt).format('MMM DD, YYYY');
        const updatedBy = row.updatedBy?.name || 'Unknown';

        return (
          <div className="smallFont">
            <div className="text-dark">{date}</div>
            <div className="text-muted tinyFont">by {updatedBy}</div>
          </div>
        );
      },
    },
    {
      key: 'actions',
      label: 'Actions',
      sortable: false,
      width: '70',
      align: 'center',
      onCellClick: () => {},
      cellRenderer: row => {
        const actionButtons = [
          { label: ACTION.EDIT, icon: <Edit2 size={14} /> },
          row.isHidden
            ? { label: ACTION.UNARCHIVE, icon: <Eye size={14} /> }
            : { label: ACTION.ARCHIVE, icon: <EyeOff size={14} /> },
          { label: ACTION.DELETE, icon: <Trash2 size={14} /> },
        ];

        return (
          <Dropdown drop="down-start" autoClose="true">
            <Dropdown.Toggle
              as={Button}
              size="sm"
              variant="outline-light"
              className="p-0 d-flex align-items-center justify-content-center border-0 text-dark dropdown-toggle-no-arrow hover-muted"
              style={{ width: 28, height: 28 }}
            >
              <Ellipsis size={14} />
            </Dropdown.Toggle>
            <Dropdown.Menu align="end" className="p-1">
              {actionButtons.map(({ label, icon }) => (
                <Dropdown.Item
                  key={label}
                  as={Button}
                  size="sm"
                  variant=""
                  className={`py-2 px-2 mr-1 d-flex align-items-center ${
                    label === ACTION.DELETE ? 'text-danger' : 'text-dark'
                  }`}
                  style={{ borderRadius: '12px' }}
                  onClick={e => {
                    e.preventDefault();
                    e.stopPropagation();
                    handleActionSelect(label, row);
                  }}
                >
                  {icon} <span className="ml-2">{label}</span>
                </Dropdown.Item>
              ))}
            </Dropdown.Menu>
          </Dropdown>
        );
      },
    },
  ];

  const handleRowClick = row => {
    onAssociationSelect(row);
  };

  const getRowStyle = row => {
    return selectedAssociationId === row._id ? { backgroundColor: 'var(--info-lighter)' } : {};
  };

  return (
    <>
      <DataTable
        key={`${sortConfig.key}-${sortConfig.direction}`}
        data={associations}
        columns={columns}
        rowKey="_id"
        loading={loading}
        onRowClick={handleRowClick}
        getRowStyle={getRowStyle}
        pagination={false}
        hover={true}
        sortable={true}
        serverSort={true}
        defaultSort={sortConfig}
        onSortChange={handleSortChange}
        responsive={false}
        maxHeight="calc(100vh - 300px)"
        emptyMessage={
          searchQuery ? `No results for "${searchQuery}". Try a different search term.` : 'No associations found.'
        }
        onBottomReached={handleLoadMore}
        loadingMoreData={loadingMore}
        bottomOffset={100}
      />

      {!loading && associations.length > 0 && (
        <div className="text-muted mt-2">
          Showing <span className="text-dark font-weight-bold">{totalCount}</span> associations
        </div>
      )}

      {editingAssociation && (
        <DirectoryAssociationModal
          mode="edit"
          associationId={resolveEntityId(editingAssociation._id)}
          show
          onHide={() => setEditingAssociation(null)}
          onSaved={handleEditSave}
        />
      )}

      <AlertModal
        show={hideCandidate !== null}
        onHide={closeHideModal}
        onDismissClick={closeHideModal}
        onContinueClick={handleConfirmHide}
        hideContinueButton={hideFromBlockedDelete && Boolean(hideCandidate?.isHidden)}
        showProgress={actionLoading}
        alertText={
          hideFromBlockedDelete
            ? 'This association is assigned to an order and cannot be deleted.' + (!hideCandidate?.isHidden ? ' Archive it instead.' : "")
            : hideCandidate?.isHidden
              ? 'Unarchive this association? It will appear in association search and can be assigned to new orders.'
              : 'Archive this association? It cannot be assigned to new orders.'
        }
        dismissButtonText={hideFromBlockedDelete ? 'Dismiss' : 'Cancel'}
        continueButtonText={hideCandidate?.isHidden ? ACTION.UNARCHIVE : ACTION.ARCHIVE}
        continueButtonVariant="warning"
      />

      <AlertModal
        show={deleteCandidate !== null}
        onHide={() => !actionLoading && setDeleteCandidate(null)}
        onDismissClick={() => !actionLoading && setDeleteCandidate(null)}
        onContinueClick={handleConfirmDelete}
        showProgress={actionLoading}
        alertText="Delete this association? Contacts used only here will also be removed."
        dismissButtonText="Cancel"
        continueButtonText="Delete"
        continueButtonVariant="danger"
      />
    </>
  );
};

export default AssociationsTable;
